import { Button, Flex } from 'antd';
import { CloseOutlined, CopyOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import type { MessageInstance } from 'antd/es/message/interface';
import type { NotificationInstance } from 'antd/es/notification/interface';

export interface AppFeedbackApis {
  message: MessageInstance;
  notification: NotificationInstance;
}
type MessageLevel = 'success' | 'info' | 'warning';
type NotificationLevel = 'success' | 'info' | 'warning';

type MessageOptions = {
  key?: string;
  /** Messages remain open unless a callsite explicitly opts into timed dismissal. */
  autoDismissAfterSeconds?: number;
};

type NotificationOptions = {
  key?: string;
  onClick?: () => void;
  actions?: ReactNode;
  duration?: number;
};

type ErrorNotificationOptions = {
  title?: string;
  description?: string;
  key?: string;
};

let antAppApis: AppFeedbackApis | null = null;
let nextMessageId = 0;

export function registerAppFeedback(apis: AppFeedbackApis): () => void {
  antAppApis = apis;
  return () => {
    if (antAppApis === apis) antAppApis = null;
  };
}

function getAntAppApis(): AppFeedbackApis {
  if (!antAppApis) {
    throw new Error('App feedback APIs are unavailable before the Ant Design App provider mounts.');
  }
  return antAppApis;
}

function getIssueMessage(issue: unknown): string | undefined {
  if (issue instanceof Error) return issue.message;
  if (typeof issue === 'string') return issue;
  if (issue && typeof issue === 'object' && 'message' in issue) {
    const { message } = issue;
    if (typeof message === 'string') return message;
  }
  return undefined;
}

function serializeIssue(issue: unknown): string {
  const serializableIssue =
    issue instanceof Error
      ? {
          name: issue.name,
          message: issue.message,
          ...(import.meta.env.DEV ? { stack: issue.stack } : {}),
          ...( 'code' in issue ? { code: issue.code } : {}),
          ...( 'cause' in issue ? { cause: issue.cause } : {}),
        }
      : issue;
  const seen = new WeakSet<object>();

  try {
    return (
      JSON.stringify(
        serializableIssue,
        (key, value: unknown) => {
          if (key === 'stack' && !import.meta.env.DEV) return undefined;
          if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) return '[Circular]';
            seen.add(value);
          }
          return value;
        },
        2,
      ) ?? String(serializableIssue)
    );
  } catch {
    return String(issue);
  }
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the synchronous clipboard API for browsers denying async access.
    }
  }
  if (typeof document === 'undefined' || !document.body) return false;

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';

  try {
    document.body.append(textArea);
    textArea.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    textArea.remove();
  }
}

async function copyIssueDetails(text: string): Promise<void> {
  const copied = await copyTextToClipboard(text);
  if (!copied) {
    appFeedback.message(
      'warning',
      'Không thể sao chép lỗi. Hãy kiểm tra quyền clipboard của trình duyệt rồi thử lại.',
    );
  }
}

export const appFeedback = {
  message(type: MessageLevel, content: ReactNode, options: MessageOptions = {}): void {
    const { message } = getAntAppApis();
    const key = options.key ?? `app-feedback-message-${++nextMessageId}`;

    message.open({
      type,
      key,
      duration: options.autoDismissAfterSeconds ?? 0,
      content: (
        <Flex align="center" gap="small">
          <div style={{ flex: 1 }}>{content}</div>
          <Button
            aria-label="Đóng thông báo"
            icon={<CloseOutlined />}
            onClick={() => message.destroy(key)}
            size="small"
            type="text"
          />
        </Flex>
      ),
    });
  },

  loading(content: ReactNode, key = `app-feedback-loading-${++nextMessageId}`): () => void {
    const { message } = getAntAppApis();
    message.open({ content, duration: 0, key, type: 'loading' });
    return () => message.destroy(key);
  },

  notification(
    type: NotificationLevel,
    title: ReactNode,
    description?: ReactNode,
    options: NotificationOptions = {},
  ): void {
    const { notification } = getAntAppApis();
    notification[type]({
      title,
      description,
      key: options.key,
      onClick: options.onClick,
      actions: options.actions,
      duration: options.duration,
    });
  },
  copyToClipboard(text: string): Promise<boolean> {
    return copyTextToClipboard(text);
  },

  error(issue: unknown, options: ErrorNotificationOptions = {}): void {
    const { notification } = getAntAppApis();
    const description = options.description ?? getIssueMessage(issue) ?? 'Đã xảy ra lỗi không mong muốn.';
    const details = [
      options.title ? `Tiêu đề: ${options.title}` : undefined,
      `Thông báo: ${description}`,
      'Chi tiết lỗi:',
      serializeIssue(issue),
    ]
      .filter(Boolean)
      .join('\n');

    notification.error({
      title: options.title ?? 'Đã xảy ra lỗi',
      description,
      key: options.key,
      actions: (
        <Button icon={<CopyOutlined />} onClick={() => void copyIssueDetails(details)} size="small" type="link">
          Sao chép lỗi
        </Button>
      ),
    });
  },

  destroyNotification(key?: string): void {
    getAntAppApis().notification.destroy(key);
  },
  spamWarning(title: ReactNode, description?: ReactNode, options: NotificationOptions = {}): void {
    appFeedback.notification('warning', title, description, options);
  },
};
